// Straightener parameter catalog — types and presentation-only constants.
// The catalog DATA (parameters, groups, scales, select options) lives in the DB
// (MILL.catStraightenerParams / StraightenerPlcTags / catStraightenerParamOptions)
// and is served by MILL.GetStraightenerParameterCatalog; fetch it with
// useStraightenerParameters(). This module only keeps what is genuinely
// front-end: group ordering, group display labels and the row types.

export type StraightenerParamGroup =
	| "Dimensions"
	| "Entry"
	| "StraighteningRolls"
	| "CoolingBed"
	| "CutToLength"
	| "SpeedDiameters";

export type StraightenerParamValueType = "numeric" | "select";

export interface StraightenerParamOption {
	value: string;
	label: string;
	/** Numeric value stored/written for this option. */
	numeric: number;
}

export interface StraightenerParameter {
	/** Stable code, also the key into recipe values. */
	code: string;
	displayName: string;
	group: StraightenerParamGroup;
	sortOrder: number;
	/** True => one value per roll (Roll #1..#rollCount). */
	isPerRoll: boolean;
	/** True => carries Min/Max tolerance bands (the monitored per-roll metrics). */
	hasMinMax: boolean;
	valueType: StraightenerParamValueType;
	/** UI value = register / scale (1 = unscaled). */
	scale: number;
	/** Readback compare tolerance; null => half a quantization step (0.5 / scale). */
	tolerance: number | null;
	/** Written to the PLC on transfer. false => stored/display only. */
	sendToPlc: boolean;
	/** PLC-calculated / read-back => not editable. */
	readOnly: boolean;
	options?: StraightenerParamOption[];
}

export const GROUP_ORDER: StraightenerParamGroup[] = [
	"Dimensions",
	"Entry",
	"StraighteningRolls",
	"CoolingBed",
	"CutToLength",
	"SpeedDiameters",
];

export const GROUP_LABELS: Record<StraightenerParamGroup, string> = {
	Dimensions: "Product Dimensions",
	Entry: "Entry / Line-up",
	StraighteningRolls: "Rolls",
	CoolingBed: "Cooling Bed",
	CutToLength: "Cut to Length",
	SpeedDiameters: "Line Speed & Diameters",
};

/** Fallback while the catalog loads; the real count is per-parameter DB data. */
export const DEFAULT_ROLL_COUNT = 5;

export const parametersByGroup = (
	parameters: StraightenerParameter[],
): Record<StraightenerParamGroup, StraightenerParameter[]> => {
	const map = {} as Record<StraightenerParamGroup, StraightenerParameter[]>;
	for (const g of GROUP_ORDER) {
		map[g] = parameters.filter((param) => param.group === g);
	}
	return map;
};

/** Label for a select parameter's stored numeric value (e.g. 1 -> "Proximity"). */
export const optionLabel = (
	p: StraightenerParameter | undefined,
	numeric: number | null | undefined,
): string | null => {
	if (!p?.options || numeric == null) return null;
	return p.options.find((o) => o.numeric === numeric)?.label ?? null;
};
